import HomeClient from 'flow/api-clients/home-client.js';
import appConfig from 'app-config';

const isLocalhost = window.location.hostname.includes('localhost');
const isGeonexConfigured = appConfig?.isGeonexConfigured;

/**
 * Calls Home's external, unauthenticated "convert" endpoint
 * (`GET /api/external/conference/convert/{sha256}`) — the same one the
 * Outlook add-in uses to obtain a fresh, unlinked video-only meeting code
 * with no chat room and no organizer attached. The hash only has to satisfy
 * the endpoint's format check; it does not need to represent anything.
 *
 * Used so a meeting scheduled via "Schedule in Outlook" gets no chat room
 * until the backend calendar sync later detects the saved Outlook event and
 * reconciles one onto it — mirroring the add-in's model instead of AC-1251's
 * previous eager room creation (see AC-1714 and the Confluence page
 * "Outlook Calendar Add-in — Architecture Overview").
 *
 * Bypasses HomeClient.exec (it's hardcoded to the `/api/internal` prefix,
 * this endpoint lives under `/api/external`) but still replicates its
 * Geonex dev-routing header — without it, a local dev environment that
 * relies on X-Target-Host to reach the right backend would silently hit
 * the wrong host instead.
 */
export const getUnlinkedMeetingCode = async (): Promise<string> => {
    const hash = generateHexHash();
    const headers: HeadersInit = {};
    if (isGeonexConfigured && HomeClient.devApiPath && isLocalhost) {
        headers['X-Target-Host'] = HomeClient.devApiPath;
    }

    const response = await fetch(
        `${HomeClient.apiHost}/api/external/conference/convert/${hash}`,
        { headers },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to create unlinked meeting code: ${response.status}`,
        );
    }
    const data = await response.json();
    return data.meetingCode;
};

const generateHexHash = (): string => {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
        '',
    );
};
